import { BuffOrDebuff, WarbandMemberDTO } from "../types";
import { getBuffName } from "./formatting";

export interface StatModifier {
    label: string;
    value: number;
    type: 'add' | 'set' | 'rule';
}

export const getStatModifiers = (member: WarbandMemberDTO, stat: string, base: number, warbandBuffs?: BuffOrDebuff[]): StatModifier[] => {
    const modifiers: StatModifier[] = [];
    let adjusted = base;

    const weapons = member.weapons ?? [];
    const armours = member.armours ?? [];

    const memberBuffs = member.buffsAndDebuffs ?? [];
    const activeWarbandBuffs = (warbandBuffs ?? []).filter(b => b.globalYou > 0);
    const allBuffs = [...memberBuffs, ...activeWarbandBuffs];

    const injuries = member.permanentInjuries ?? [];

    switch (stat) {
        case 'move':
            allBuffs.forEach(buff => {
                if (buff.moveChangeType === 'add' && buff.moveChangeValue != null) {
                    const delta = buff.moveChangeValue * (buff.stackCount ?? 1);
                    modifiers.push({ label: getBuffName(buff), value: delta, type: 'add' });
                    adjusted += delta;
                }
            });
            allBuffs.forEach(buff => {
                if (buff.moveChangeType === 'set' && buff.moveChangeValue != null) {
                    modifiers.push({ label: getBuffName(buff), value: buff.moveChangeValue, type: 'set' });
                    adjusted = buff.moveChangeValue;
                }
            });
            injuries.forEach(injury => {
                if (injury.moveChange != null) {
                    modifiers.push({ label: injury.name, value: injury.moveChange, type: 'add' });
                    adjusted += injury.moveChange;
                }
            });
            if (member.carryingTreasure) {
                modifiers.push({ label: 'Carrying Treasure', value: -(adjusted / 2), type: 'rule' });
            }
            break;

        case 'fight': {
            allBuffs.forEach(buff => {
                if (buff.fightChangeType === 'add' && buff.fightChangeValue != null) {
                    const delta = buff.fightChangeValue * (buff.stackCount ?? 1);
                    modifiers.push({ label: getBuffName(buff), value: delta, type: 'add' });
                    adjusted += delta;
                }
            });
            allBuffs.forEach(buff => {
                if (buff.fightChangeType === 'set' && buff.fightChangeValue != null) {
                    modifiers.push({ label: getBuffName(buff), value: buff.fightChangeValue, type: 'set' });
                    adjusted = buff.fightChangeValue;
                }
            });
            injuries.forEach(injury => {
                if (injury.fightChange != null) {
                    modifiers.push({ label: injury.name, value: injury.fightChange, type: 'add' });
                    adjusted += injury.fightChange;
                }
            });
            const hasWeaponWithFightModifier = weapons.some(w => w.encumbering !== null && w.encumbering > 0);
            const hasArmourWithFightModifier = armours.some(a => a.encumbering !== null && a.encumbering > 0);
            if (member.carryingTreasure && (hasWeaponWithFightModifier || hasArmourWithFightModifier)) {
                modifiers.push({ label: 'Carrying Treasure', value: -1, type: 'rule' });
            }
            break;
        }

        case 'shoot':
            allBuffs.forEach(buff => {
                if (buff.shootChangeType === 'add' && buff.shootChangeValue != null) {
                    const delta = buff.shootChangeValue * (buff.stackCount ?? 1);
                    modifiers.push({ label: getBuffName(buff), value: delta, type: 'add' });
                    adjusted += delta;
                }
            });
            allBuffs.forEach(buff => {
                if (buff.shootChangeType === 'set' && buff.shootChangeValue != null) {
                    modifiers.push({ label: getBuffName(buff), value: buff.shootChangeValue, type: 'set' });
                    adjusted = buff.shootChangeValue;
                }
            });
            injuries.forEach(injury => {
                if (injury.shootChange != null) {
                    modifiers.push({ label: injury.name, value: injury.shootChange, type: 'add' });
                    adjusted += injury.shootChange;
                }
            });
            break;

        case 'armour': {
            allBuffs.forEach(buff => {
                if (buff.armourChangeType === 'add' && buff.armourChangeValue != null) {
                    const delta = buff.armourChangeValue * (buff.stackCount ?? 1);
                    modifiers.push({ label: getBuffName(buff), value: delta, type: 'add' });
                    adjusted += delta;
                }
            });
            allBuffs.forEach(buff => {
                if (buff.armourChangeType === 'set' && buff.armourChangeValue != null) {
                    modifiers.push({ label: getBuffName(buff), value: buff.armourChangeValue, type: 'set' });
                    adjusted = buff.armourChangeValue;
                }
            });

            break;
        }

        case 'will':
            allBuffs.forEach(buff => {
                if (buff.willChangeType === 'add' && buff.willChangeValue != null) {
                    const delta = buff.willChangeValue * (buff.stackCount ?? 1);
                    modifiers.push({ label: getBuffName(buff), value: delta, type: 'add' });
                    adjusted += delta;
                }
            });
            allBuffs.forEach(buff => {
                if (buff.willChangeType === 'set' && buff.willChangeValue != null) {
                    modifiers.push({ label: getBuffName(buff), value: buff.willChangeValue, type: 'set' });
                    adjusted = buff.willChangeValue;
                }
            });
            injuries.forEach(injury => {
                if (injury.willChange != null) {
                    modifiers.push({ label: injury.name, value: injury.willChange, type: 'add' });
                    adjusted += injury.willChange;
                }
            });
            break;

        case 'health':
            allBuffs.forEach(buff => {
                if (buff.healthChangeType === 'add' && buff.healthChangeValue != null) {
                    const delta = buff.healthChangeValue * (buff.stackCount ?? 1);
                    modifiers.push({ label: getBuffName(buff), value: delta, type: 'add' });
                    adjusted += delta;
                }
            });
            allBuffs.forEach(buff => {
                if (buff.healthChangeType === 'set' && buff.healthChangeValue != null) {
                    modifiers.push({ label: getBuffName(buff), value: buff.healthChangeValue, type: 'set' });
                    adjusted = buff.healthChangeValue;
                }
            });
            injuries.forEach(injury => {
                if (injury.healthChange != null) {
                    modifiers.push({ label: injury.name, value: injury.healthChange, type: 'add' });
                    adjusted += injury.healthChange;
                }
            });
            break;

        default:
            break;
    }

    return modifiers;
};

export const getSplitStatValue = (member: WarbandMemberDTO, stat: string, base: number, warbandBuffs?: BuffOrDebuff[]) => {
    let adjusted = base;

    const weapons = member.weapons ?? [];
    const armours = member.armours ?? [];

    // Combine member buffs with warband-wide buffs that apply to friendly members
    const memberBuffs = member.buffsAndDebuffs ?? [];
    const activeWarbandBuffs = (warbandBuffs ?? []).filter(b => b.globalYou > 0);
    const allBuffs = [...memberBuffs, ...activeWarbandBuffs];

    const injuries = member.permanentInjuries ?? [];

    switch (stat) {
        case 'move':
            allBuffs.forEach(buff => {
                if (buff.moveChangeType === 'add') {
                    adjusted += (buff?.moveChangeValue ?? 0) * (buff.stackCount ?? 1);
                }
            });

            allBuffs.forEach(buff => {
                if (buff.moveChangeType === 'set') {
                    adjusted = buff?.moveChangeValue ?? adjusted;
                }
            });

            injuries.forEach(injury => {
                if (injury.moveChange != null) adjusted += injury.moveChange;
            });

            adjusted = member.carryingTreasure ? adjusted / 2 : adjusted;
            break;
        case 'fight': {
            allBuffs.forEach(buff => {
                if (buff.fightChangeType === 'add') {
                    adjusted += (buff?.fightChangeValue ?? 0) * (buff.stackCount ?? 1);
                }
            });

            allBuffs.forEach(buff => {
                if (buff.fightChangeType === 'set') {
                    adjusted = buff?.fightChangeValue ?? adjusted;
                }
            });

            injuries.forEach(injury => {
                if (injury.fightChange != null) adjusted += injury.fightChange;
            });

            const hasWeaponWithFightModifier = weapons.some(w => w.encumbering !== null && w.encumbering > 0);
            const hasArmourWithFightModifier = armours.some(a => a.encumbering !== null && a.encumbering > 0);
            adjusted = member.carryingTreasure && (hasWeaponWithFightModifier || hasArmourWithFightModifier) ? adjusted - 1 : adjusted;
            break;
        }
        case 'shoot':
            allBuffs.forEach(buff => {
                if (buff.shootChangeType === 'add') {
                    adjusted += (buff?.shootChangeValue ?? 0) * (buff.stackCount ?? 1);
                }
            });

            allBuffs.forEach(buff => {
                if (buff.shootChangeType === 'set') {
                    adjusted = buff?.shootChangeValue ?? adjusted;
                }
            });

            injuries.forEach(injury => {
                if (injury.shootChange != null) adjusted += injury.shootChange;
            });
            break;
        case 'armour': {
            allBuffs.forEach(buff => {
                if (buff.armourChangeType === 'add') {
                    adjusted += (buff?.armourChangeValue ?? 0) * (buff.stackCount ?? 1);
                }
            });

            allBuffs.forEach(buff => {
                if (buff.armourChangeType === 'set') {
                    adjusted = buff?.armourChangeValue ?? adjusted;
                }
            });

            break;
        }
        case 'will':
            allBuffs.forEach(buff => {
                if (buff.willChangeType === 'add') {
                    adjusted += (buff?.willChangeValue ?? 0) * (buff.stackCount ?? 1);
                }
            });

            allBuffs.forEach(buff => {
                if (buff.willChangeType === 'set') {
                    adjusted = buff?.willChangeValue ?? adjusted;
                }
            });

            injuries.forEach(injury => {
                if (injury.willChange != null) adjusted += injury.willChange;
            });
            break;
        case 'health':
            allBuffs.forEach(buff => {
                if (buff.healthChangeType === 'add') {
                    adjusted += (buff?.healthChangeValue ?? 0) * (buff.stackCount ?? 1);
                }
            });

            allBuffs.forEach(buff => {
                if (buff.healthChangeType === 'set') {
                    adjusted = buff?.healthChangeValue ?? adjusted;
                }
            });

            injuries.forEach(injury => {
                if (injury.healthChange != null) adjusted += injury.healthChange;
            });
            break;
        default:
            break;
    }

    if (adjusted === undefined || adjusted === base) {
        return base;
    }

    return adjusted;
}
